import React, { useCallback, useRef, useState } from "react";
import { Field } from "redux-form";
import { useDrag, useDrop } from "react-dnd";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Switch } from "antd";
import moment from "moment";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { EditorState, ContentState, convertToRaw } from "draft-js";

import emailEditor from "../../../../common/EmailEditor";

import styles from "../../JobStyle.module.css";
import "./JobSwmsFormStyle.css";

const DRAG_TYPE = "SWMS_AUTH_SIGNED_ROW";

/* ── Draggable row for "signed" approvers ── */
function DraggableSignedRow({ approver, index, moveRow, afterDrop, loading, onToggleInDocument }) {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: DRAG_TYPE,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ ...approver, index }),
    canDrag: !loading,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    end: (item, monitor) => {
      if (monitor.didDrop()) afterDrop(item);
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={preview}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      data-handler-id={handlerId}
    >
      <td style={{ textAlign: "center" }}>
        <span
          ref={ref}
          style={{ cursor: loading ? "not-allowed" : "grab", color: "#94a3b8", display: "inline-block" }}
          title="Drag to reorder"
        >
          <svg xmlns="http://www.w3.org/2000/svg" style={{ height: "14px", width: "14px" }} viewBox="0 0 320 512">
            <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
          </svg>
        </span>
      </td>
      <td style={{ fontWeight: "600" }}>{approver.name || ""}</td>
      <td>{approver.position || ""}</td>
      <td>
        <span className="swms-auth-status-badge swms-auth-badge-approved">Approved</span>
      </td>
      <td>
        {approver.date ? moment.utc(approver.date).format("DD/MM/YYYY") : ""}
      </td>
      <td>
        {approver.signature ? (
          <img
            src={approver.signature}
            alt="signature"
            style={{ height: "30px", maxWidth: "100%", objectFit: "contain" }}
          />
        ) : (
          <span>—</span>
        )}
      </td>
      <td style={{ textAlign: "center" }}>
        <Switch
          size="small"
          className="cs-switch-v2"
          checked={!!approver.in_document}
          onChange={() => onToggleInDocument(index)}
          disabled={loading}
        />
      </td>
    </tr>
  );
}

/* ── Main section component ── */
const AuthorisationDetailsSection = ({ approvers = [], loading, onReorderApprovers, onToggleInDocument, change, notesFieldName = "authorisation_notes" }) => {
  const [signedApprovers, setSignedApprovers] = useState(null);

  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  const onEditorStateChange = useCallback((newState) => {
    setEditorState(newState);
    if (change) {
      change(notesFieldName, draftToHtml(convertToRaw(newState.getCurrentContent())));
    }
  }, [change, notesFieldName]);

  // Derive signed list — either from local drag state or from props
  const resolveSignedApprovers = () => {
    if (signedApprovers !== null) return signedApprovers;
    return (approvers || []).filter((a) => a.role === "signed");
  };

  const moveRow = useCallback((dragIndex, hoverIndex) => {
    setSignedApprovers((prev) => {
      const list = prev !== null ? prev : (approvers || []).filter((a) => a.role === "signed");
      const updated = [...list];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, moved);
      return updated;
    });
  }, [approvers]);

  const afterDrop = useCallback((item) => {
    if (onReorderApprovers) {
      onReorderApprovers(resolveSignedApprovers());
    }
  }, [signedApprovers, onReorderApprovers]);

  const handleToggleInDocument = useCallback((index) => {
    if (onToggleInDocument) onToggleInDocument(index);
  }, [onToggleInDocument]);

  const creatorRows = (approvers || []).filter((a) => a.role === "creator");
  const pendingRows = (approvers || []).filter((a) => a.role === "pending");
  const currentSigned = resolveSignedApprovers();
  const totalRows = creatorRows.length + currentSigned.length + pendingRows.length;

  return (
    <div className="my-2">
      <div className="container-fluid px-0">
        {/* Authorisation Notes — rich text */}
        <div className="row mb-2">
          <div className="col-12">
            <div className={styles.Label}>Authorisation Notes</div>
            <Field
              name="authorisation_notes"
              component={emailEditor}
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              disabled={loading}
            />
          </div>
        </div>

        {/* Approvers Table */}
        <div className="row">
          <div className="col-12">
            <DndProvider backend={HTML5Backend}>
              <table
                className="table-sm cs-table cs-table-v2 cs-table-job-swms"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "20%" }}>Name</th>
                    <th style={{ width: "18%" }}>Position</th>
                    <th style={{ width: "12%" }}>Action</th>
                    <th style={{ width: "12%" }}>Date</th>
                    <th style={{ width: "22%" }}>Signature</th>
                    <th style={{ width: "12%", textAlign: "center" }}>In Document</th>
                  </tr>
                </thead>
                <tbody>
                  {totalRows > 0 ? (
                    <>
                      {/* Creator row — always first, not draggable */}
                      {creatorRows.map((approver, i) => (
                        <tr key={`creator-${i}`}>
                          <td style={{ textAlign: "center" }}>
                            <i
                              className="fa fa-lock"
                              style={{ color: "#cbd5e1", fontSize: "12px" }}
                              title="Creator is always included"
                            />
                          </td>
                          <td style={{ fontWeight: "600" }}>{approver.name || ""}</td>
                          <td>{approver.position || ""}</td>
                          <td>
                            <span className="swms-auth-status-badge swms-auth-badge-prepared">Prepared</span>
                          </td>
                          <td>
                            {approver.date ? moment.utc(approver.date).format("DD/MM/YYYY") : ""}
                          </td>
                          <td>
                            {approver.signature ? (
                              <img
                                src={approver.signature}
                                alt="signature"
                                style={{ height: "30px", maxWidth: "100%", objectFit: "contain" }}
                              />
                            ) : (
                              <span>—</span>
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <span className="swms-auth-in-doc-badge">Always</span>
                          </td>
                        </tr>
                      ))}

                      {/* Signed (approved) rows — draggable */}
                      {currentSigned.map((approver, i) => (
                        <DraggableSignedRow
                          key={`signed-${i}`}
                          approver={approver}
                          index={i}
                          moveRow={moveRow}
                          afterDrop={afterDrop}
                          loading={loading}
                          onToggleInDocument={handleToggleInDocument}
                        />
                      ))}

                      {/* Pending rows — not draggable */}
                      {pendingRows.map((approver, i) => (
                        <tr key={`pending-${i}`}>
                          <td style={{ textAlign: "center" }}>
                            <i
                              className="fa fa-clock-o"
                              style={{ color: "#93c5fd", fontSize: "12px" }}
                            />
                          </td>
                          <td style={{ fontWeight: "600", color: "#64748b" }}>{approver.name || ""}</td>
                          <td style={{ color: "#94a3b8" }}>{approver.position || ""}</td>
                          <td>
                            <span className="swms-auth-status-badge swms-auth-badge-pending">Pending</span>
                          </td>
                          <td style={{ color: "#94a3b8", fontStyle: "italic" }}>Awaiting response</td>
                          <td style={{ color: "#94a3b8" }}>—</td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              className="swms-auth-resend-btn"
                              disabled={loading}
                              type="button"
                            >
                              Resend Link
                            </button>
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center" style={{ color: "#94a3b8" }}>
                        No approvers available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DndProvider>
            <p className="swms-auth-help-text">
              Only toggled-on approvals from the current version will appear in the SWMS sent to clients.
              The creator is always included.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorisationDetailsSection;
